import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { Camera, X, Loader2, AlertTriangle, CheckCircle, CloudOff, Maximize2, RefreshCw, Plus } from 'lucide-react';
import { apiClient, api, getApiErrorMessage } from '../api/client';
import { putToPresignedUrl, isUploadFailedError } from '../api/upload';
import { Camera as CapCamera, CameraResultType, CameraSource } from '@capacitor/camera';
import { isNative } from '../lib/platform';
import { useSyncStore } from '../lib/offline/sync-store';
import { queueUpload } from '../lib/offline/upload-sync';
import { compressImage } from '../lib/offline/image-compress';
import { v4 as uuidv4 } from 'uuid';
import { Tooltip } from './ui/tooltip';

interface UploadedPhoto {
  key: string;
  url: string;
  label?: string;
}

interface PhotoSlot {
  label: string;
  icon?: React.ReactNode;
}

interface PhotoUploaderProps {
  /** Entity type for presigned URL (e.g. 'equipment') */
  entityType: 'equipment' | 'job' | 'safety' | 'employee' | 'ticket' | 'inspections' | 'maintenance' | 'expense_receipt';
  /** Entity ID for presigned URL */
  entityId: string;
  /** Called whenever the photo list changes */
  onChange: (photos: UploadedPhoto[]) => void;
  /** Current photos */
  value?: UploadedPhoto[];
  /** Optional: predefined photo slots with labels */
  slots?: PhotoSlot[];
  /** Optional: max number of photos (default 6) */
  maxPhotos?: number;
  /** Optional: label */
  label?: string;
  /**
   * Render mode. Default 'slots' — the fixed Overview/Issue/Repair category tiles
   * used by inspection, safety and equipment screens.
   * 'grid' renders a compact responsive thumbnail grid with a small "+" add tile
   * and no category slots (used by the field ticket screen).
   */
  variant?: 'slots' | 'grid';
  /** @deprecated Use `variant="grid"` instead. Kept so existing callers keep working. */
  freeform?: boolean;
}

export default function PhotoUploader({
  entityType,
  entityId,
  onChange,
  value = [],
  slots,
  maxPhotos = 6,
  label = 'Photos',
  variant,
  freeform = false,
}: PhotoUploaderProps) {
  // `variant` wins when supplied; otherwise fall back to the legacy `freeform` flag.
  // Both unset => 'slots', preserving the original Overview/Issue/Repair behaviour.
  const mode: 'slots' | 'grid' = variant ?? (freeform ? 'grid' : 'slots');
  const isOnline = useSyncStore((s) => s.isOnline);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingSlot, setUploadingSlot] = useState<string | undefined>();
  const [error, setError] = useState('');
  const [activeSlot, setActiveSlot] = useState<string | undefined>();
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  // Fresh signed URLs keyed by R2 key — avoids expired presigned URLs on draft reload
  const [resolvedUrls, setResolvedUrls] = useState<Record<string, string>>({});

  // Keys whose <img> failed to load. Rendered as a labelled placeholder rather
  // than the browser's broken-image glyph: a broken <img> has no intrinsic size,
  // so `w-full h-full` collapses and the alt text spills out of the tile — which
  // is what made the Photos row look mis-sized on a 375px screen.
  const [failedKeys, setFailedKeys] = useState<Record<string, true>>({});

  // Stable fingerprint of non-pending photo keys — changes only when actual photos change,
  // not on every render (avoids infinite refetch from unstable array references)
  const photoKeyFingerprint = useMemo(
    () => value.filter((p) => !p.key.startsWith('pending-')).map((p) => p.key).sort().join(','),
    [value],
  );

  // The uploads list is the only source of a URL a browser can actually load.
  //
  // Two shapes have to line up here and one of them is not obvious. The presign
  // response's `fileUrl` is NOT a URL — it is the R2 object key, byte-identical
  // to `key` (verified against the API on 2026-08-04: a 140-char
  // `<companyId>/<entityType>/<entityId>/<uuid>.jpg`, no scheme, no host). Rows
  // from GET /uploads/:entityType/:entityId are camelCase and carry that key in
  // `s3Key`; there is no `key` field on them at all. `url` is the presigned GET.
  //
  // Reading `u.key` therefore matched nothing, `urlMap` stayed empty, and every
  // freshly uploaded thumbnail kept the object key as its `src` — a relative URL
  // that 404s against the app origin. That is the broken-image placeholder, and
  // because the refresh never landed a real URL it never healed.
  const uploadKeyOf = (u: any): string | undefined => u?.s3Key ?? u?.key;

  const fetchUrlMap = useCallback(async (): Promise<Record<string, string>> => {
    const res = await api.uploads.listForEntity(entityType, entityId);
    const uploads: any[] = res.data?.data ?? res.data ?? [];
    if (!Array.isArray(uploads)) return {};
    const urlMap: Record<string, string> = {};
    for (const u of uploads) {
      const key = uploadKeyOf(u);
      const url = u.url || u.presigned_url;
      if (key && url) urlMap[key] = url;
    }
    return urlMap;
  }, [entityType, entityId]);

  const refreshUrls = useCallback(async () => {
    if (!entityId || !isOnline) return;
    const nonPendingKeys = photoKeyFingerprint.split(',').filter(Boolean);
    if (nonPendingKeys.length === 0) return;

    try {
      const urlMap = await fetchUrlMap();
      setResolvedUrls((prev) => ({ ...prev, ...urlMap }));
      // A key that just resolved gets another chance to render.
      setFailedKeys((prev) => {
        const next = { ...prev };
        for (const key of Object.keys(urlMap)) delete next[key];
        return next;
      });
    } catch {
      // If refresh fails, fall back to original URLs (may be stale)
    }
  }, [entityId, isOnline, photoKeyFingerprint, fetchUrlMap]);

  // Refresh URLs on mount, when entityId changes, or when photos populate after async load
  useEffect(() => {
    refreshUrls();
  }, [refreshUrls]);

  // Helper: get the best available URL for a photo
  const getPhotoUrl = useCallback(
    (photo: UploadedPhoto) => resolvedUrls[photo.key] || photo.url,
    [resolvedUrls],
  );

  // Object URLs minted as a stand-in when the signed URL could not be fetched.
  // Held so they can be revoked on unmount — at most `maxPhotos` of them.
  const localPreviewsRef = useRef<string[]>([]);
  useEffect(() => {
    const previews = localPreviewsRef.current;
    return () => {
      previews.forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch {
          /* jsdom and older WebViews may not implement it */
        }
      });
    };
  }, []);

  const createLocalPreview = (file: File): string | undefined => {
    try {
      const url = URL.createObjectURL(file);
      localPreviewsRef.current.push(url);
      return url;
    } catch {
      return undefined;
    }
  };

  const base64ToBlob = (base64: string, mimeType: string): Blob => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    return new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
  };

  const queueOfflineUpload = async (file: File) => {
    const compressed = await compressImage(file);
    const localUrl = await fileToBase64(new File([compressed], file.name, { type: 'image/jpeg' }));
    const uploadId = uuidv4();

    await queueUpload({
      id: uploadId,
      entityType,
      entityId,
      fileName: file.name,
      contentType: 'image/jpeg',
      blob: compressed,
      label: activeSlot,
      localUrl,
      status: 'pending',
      retryCount: 0,
      createdAt: new Date().toISOString(),
    });

    const { setPendingUploads, pendingUploads } = useSyncStore.getState();
    setPendingUploads(pendingUploads + 1);

    const newPhoto: UploadedPhoto = {
      key: `pending-${uploadId}`,
      url: localUrl,
      label: activeSlot,
    };
    onChange([...value, newPhoto]);
  };

  const uploadFile = async (file: File) => {
    setUploading(true);
    setUploadingSlot(activeSlot);
    setError('');

    if (!isOnline) {
      try {
        await queueOfflineUpload(file);
      } catch {
        setError('Failed to save photo offline.');
        setTimeout(() => setError(''), 3000);
      } finally {
        setUploading(false);
        setUploadingSlot(undefined);
        setActiveSlot(undefined);
      }
      return;
    }

    try {
      const { data: presigned } = await apiClient.post('/uploads/presigned-url', {
        fileName: file.name,
        contentType: file.type,
        entityType,
        entityId,
      });

      // Throws unless S3 answered 2xx — /uploads/confirm below is only reached
      // for bytes that are actually in the bucket.
      await putToPresignedUrl(presigned.uploadUrl, file, file.type);

      await apiClient.post('/uploads/confirm', {
        key: presigned.key,
        size: file.size,
      });

      // `presigned.fileUrl` is the object key, not a URL (see uploadKeyOf above),
      // so it cannot be the thumbnail's src. Ask the uploads list for a real
      // presigned GET before putting the photo on screen — the spinner is still
      // up, so this round trip costs nothing the user can see.
      let displayUrl: string | undefined;
      try {
        displayUrl = (await fetchUrlMap())[presigned.key];
      } catch {
        // Fall through to the local preview below.
      }

      // The bytes are in the bucket either way — the confirm above succeeded.
      // If the URL lookup failed, show the file the user just picked rather than
      // a src that cannot load; the refresh effect swaps in a signed URL on its
      // next pass, and resolvedUrls takes precedence over this.
      if (!displayUrl) displayUrl = createLocalPreview(file);

      if (displayUrl) {
        setResolvedUrls((prev) => ({ ...prev, [presigned.key]: displayUrl as string }));
      }

      const newPhoto: UploadedPhoto = {
        key: presigned.key,
        url: displayUrl ?? '',
        label: activeSlot,
      };
      onChange([...value, newPhoto]);
    } catch (err: any) {
      console.error('Photo upload failed:', err);

      // Storage rejected the bytes (4xx on the PUT). Re-sending the same file
      // will be rejected the same way, and queueing it would put a photo on
      // screen with a "waiting to sync" badge that is never going to clear.
      // Say so instead, and add nothing to the list — the user still has the
      // photo on their camera roll and can retry deliberately.
      //
      // NOTE: the error is left on screen rather than auto-cleared after 3s.
      // A validation message ("max 6 photos") can time out; a photo that did
      // not save cannot, because the timeout is exactly how the user comes to
      // believe it did.
      if (isUploadFailedError(err) && err.kind === 'rejected') {
        setError(getApiErrorMessage(err, 'Upload was rejected. Please try again.'));
        return;
      }

      // Everything else — no response, a timeout, a 5xx, a failed presign — is
      // a connectivity-shaped failure. The offline queue is exactly the right
      // home for it: the blob is kept, the flush re-signs, and the pending
      // count shows the user it has not landed yet.
      const recoverable =
        isUploadFailedError(err) ||
        err?.response?.status >= 500 ||
        err?.message?.includes('Network') ||
        !err?.response;

      if (recoverable) {
        try {
          await queueOfflineUpload(file);
        } catch {
          setError('Failed to save photo. Please try again.');
        }
      } else {
        setError(getApiErrorMessage(err, 'Upload failed. Please try again.'));
      }
    } finally {
      setUploading(false);
      setUploadingSlot(undefined);
      setActiveSlot(undefined);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file.');
      setTimeout(() => setError(''), 3000);
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('Image must be under 10MB.');
      setTimeout(() => setError(''), 3000);
      return;
    }

    await uploadFile(file);
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleRemove = (index: number) => {
    const updated = value.filter((_, i) => i !== index);
    onChange(updated);
  };

  const triggerUpload = async (slotLabel?: string) => {
    if (value.length >= maxPhotos) {
      setError(`Maximum ${maxPhotos} photos allowed.`);
      setTimeout(() => setError(''), 3000);
      return;
    }
    setActiveSlot(slotLabel);

    if (isNative) {
      try {
        const photo = await CapCamera.getPhoto({
          quality: 80,
          allowEditing: false,
          resultType: CameraResultType.Base64,
          source: CameraSource.Prompt,
          width: 1920,
          height: 1080,
        });
        const mimeType = `image/${photo.format}`;
        const blob = base64ToBlob(photo.base64String!, mimeType);
        const file = new File([blob], `photo-${Date.now()}.${photo.format}`, { type: mimeType });
        await uploadFile(file);
      } catch (err: any) {
        if (err?.message?.includes('cancelled') || err?.message?.includes('canceled')) return;
        setError('Camera failed. Please try again.');
        setTimeout(() => setError(''), 3000);
      }
    } else {
      fileInputRef.current?.click();
    }
  };

  const defaultSlots: PhotoSlot[] = slots || [
    { label: 'Overview', icon: <Camera size={20} className="text-fieldiq-muted" /> },
    { label: 'Issue', icon: <AlertTriangle size={20} className="text-fieldiq-muted" /> },
    { label: 'Repair', icon: <CheckCircle size={20} className="text-fieldiq-muted" /> },
  ];

  // Get photos for a specific category
  const photosForSlot = (slotLabel: string) => value.filter((p) => p.label === slotLabel);
  // Photos without a category label (from freeform or "More" uploads)
  const uncategorizedPhotos = value.filter((p) => !p.label);

  return (
    <div className="bg-fieldiq-card rounded-xl p-4">
      <h3 className="font-semibold mb-3 flex items-center gap-2 text-white">
        <Camera size={18} />
        {label}
      </h3>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        aria-label="Choose a photo file"
        onChange={handleFileSelect}
      />

      {error && (
        <div className="text-sm text-fieldiq-danger bg-red-900/20 rounded-lg px-3 py-2 mb-3">
          {error}
        </div>
      )}

      {mode === 'grid' ? (
        /* ── Grid mode: compact responsive thumbnails + small "+" add tile ── */
        <>
          {/* Column counts step 3 → 4 → 6; the max-w cap keeps each cell a true
              thumbnail (~110-150px) instead of ballooning on full-width desktop. */}
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 max-w-md sm:max-w-lg md:max-w-2xl">
            {value.map((photo, idx) => {
              const photoSrc = getPhotoUrl(photo);
              return (
              <div key={photo.key} className="relative group aspect-square overflow-hidden rounded-lg">
                {!photoSrc || failedKeys[photo.key] ? (
                  <div className="w-full h-full rounded-lg bg-fieldiq-dark border-2 border-fieldiq-border flex flex-col items-center justify-center gap-1 px-1 text-center">
                    <AlertTriangle size={18} className="text-fieldiq-muted" />
                    <span className="text-[10px] leading-tight text-fieldiq-muted">Preview unavailable</span>
                  </div>
                ) : (
                  <img
                    src={photoSrc}
                    alt={`Attachment ${idx + 1}`}
                    className="w-full h-full object-cover rounded-lg cursor-pointer"
                    onClick={() => setLightboxUrl(photoSrc)}
                    onError={() => setFailedKeys((prev) => ({ ...prev, [photo.key]: true }))}
                  />
                )}
                {photo.key.startsWith('pending-') && (
                  <span className="absolute top-1 left-1 bg-orange-500/80 rounded-full p-0.5" title="Waiting to upload">
                    <CloudOff size={10} className="text-white" />
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => setLightboxUrl(photoSrc)}
                  className="absolute bottom-1 right-1 w-6 h-6 bg-black/60 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 sm:opacity-100 transition-opacity"
                >
                  <Maximize2 size={12} className="text-white" />
                </button>
                <button
                  type="button"
                  onClick={() => handleRemove(idx)}
                  className="absolute top-1 right-1 w-5 h-5 bg-black/60 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 sm:opacity-100 transition-opacity"
                >
                  <X size={12} className="text-white" />
                </button>
              </div>
              );
            })}
            {value.length < maxPhotos && !uploading && (
              <Tooltip content="Add photo">
                <button
                  type="button"
                  onClick={() => triggerUpload()}
                  aria-label="Add photo"
                  className="w-full aspect-square border border-dashed border-fieldiq-border rounded-lg flex items-center justify-center text-fieldiq-muted hover:border-fieldiq-accent hover:text-fieldiq-accent transition"
                >
                  <Plus size={18} />
                </button>
              </Tooltip>
            )}
          </div>
          {uploading && (
            <div className="flex items-center justify-center gap-2 py-4 text-fieldiq-accent">
              <Loader2 size={20} className="animate-spin" />
              <span className="text-sm">Uploading...</span>
            </div>
          )}
          {value.length >= maxPhotos && (
            <p className="text-xs text-fieldiq-muted text-center py-2 mt-2">Maximum photos reached</p>
          )}
        </>
      ) : (
        /* ── Category tile mode: each slot is its own upload+preview unit ── */
        <>
          <div className="grid grid-cols-3 gap-3">
            {defaultSlots.map((slot) => {
              const slotPhotos = photosForSlot(slot.label);
              const hasPhoto = slotPhotos.length > 0;
              const isUploadingThis = uploading && uploadingSlot === slot.label;

              if (isUploadingThis) {
                return (
                  <div
                    key={slot.label}
                    className="border-2 border-dashed border-fieldiq-accent/50 rounded-lg flex flex-col items-center justify-center aspect-square"
                  >
                    <Loader2 size={20} className="animate-spin text-fieldiq-accent mb-1" />
                    <span className="text-[10px] text-fieldiq-accent">Uploading...</span>
                  </div>
                );
              }

              if (hasPhoto) {
                const primaryPhoto = slotPhotos[0];
                const primarySrc = getPhotoUrl(primaryPhoto);
                const primaryFailed = !primarySrc || !!failedKeys[primaryPhoto.key];
                return (
                  <div key={slot.label} className="relative group aspect-square overflow-hidden rounded-lg">
                    {primaryFailed ? (
                      <div className="w-full h-full rounded-lg bg-fieldiq-dark border-2 border-fieldiq-border flex flex-col items-center justify-center gap-1 px-1 text-center">
                        <AlertTriangle size={18} className="text-fieldiq-muted" />
                        <span className="text-[10px] leading-tight text-fieldiq-muted">Preview unavailable</span>
                      </div>
                    ) : (
                      <img
                        src={primarySrc}
                        alt={slot.label}
                        className="w-full h-full object-cover rounded-lg cursor-pointer"
                        onClick={() => setLightboxUrl(primarySrc)}
                        onError={() => setFailedKeys((prev) => ({ ...prev, [primaryPhoto.key]: true }))}
                      />
                    )}
                    {/* Category label badge */}
                    <span className="absolute top-1 left-1 text-[10px] bg-black/70 text-white px-1.5 py-0.5 rounded font-medium">
                      {slot.label}
                    </span>
                    {/* Pending sync indicator */}
                    {primaryPhoto.key.startsWith('pending-') && (
                      <span className="absolute top-1 right-7 bg-orange-500/80 rounded-full p-0.5" title="Waiting to upload">
                        <CloudOff size={10} className="text-white" />
                      </span>
                    )}
                    {/* Multi-photo count badge */}
                    {slotPhotos.length > 1 && (
                      <span className="absolute bottom-1 left-1 text-[10px] bg-black/70 text-white px-1.5 py-0.5 rounded font-medium">
                        +{slotPhotos.length - 1} more
                      </span>
                    )}
                    {/* View full-size button */}
                    <button
                      type="button"
                      onClick={() => setLightboxUrl(primarySrc)}
                      className="absolute bottom-1 right-1 w-6 h-6 bg-black/60 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 sm:opacity-100 transition-opacity"
                    >
                      <Maximize2 size={12} className="text-white" />
                    </button>
                    {/* Replace / add another photo */}
                    {value.length < maxPhotos && (
                      <Tooltip content="Add another photo">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            triggerUpload(slot.label);
                          }}
                          className="absolute top-1 right-1 w-6 h-6 bg-black/60 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 sm:opacity-100 transition-opacity"
                          aria-label="Add another photo"
                        >
                          <RefreshCw size={11} className="text-white" />
                        </button>
                      </Tooltip>
                    )}
                  </div>
                );
              }

              // Empty state: dashed border upload target
              return (
                <button
                  key={slot.label}
                  type="button"
                  onClick={() => triggerUpload(slot.label)}
                  disabled={value.length >= maxPhotos}
                  className={`border-2 border-dashed rounded-lg flex flex-col items-center justify-center aspect-square transition ${
                    value.length >= maxPhotos
                      ? 'border-fieldiq-border/50 opacity-40 cursor-not-allowed'
                      : 'border-fieldiq-border hover:border-fieldiq-accent'
                  }`}
                >
                  {slot.icon || <Camera size={20} className="text-fieldiq-muted" />}
                  <span className="text-xs text-fieldiq-text mt-1">{slot.label}</span>
                </button>
              );
            })}
          </div>

          {/* Show any extra photos added via "More" (uncategorized) as a row below */}
          {uncategorizedPhotos.length > 0 && (
            <div className="grid grid-cols-3 gap-3 mt-3">
              {uncategorizedPhotos.map((photo) => {
                const idx = value.indexOf(photo);
                const photoSrc = getPhotoUrl(photo);
                return (
                  <div key={photo.key} className="relative group aspect-square overflow-hidden rounded-lg">
                    {!photoSrc || failedKeys[photo.key] ? (
                      <div className="w-full h-full rounded-lg bg-fieldiq-dark border-2 border-fieldiq-border flex flex-col items-center justify-center gap-1 px-1 text-center">
                        <AlertTriangle size={18} className="text-fieldiq-muted" />
                        <span className="text-[10px] leading-tight text-fieldiq-muted">Preview unavailable</span>
                      </div>
                    ) : (
                      <img
                        src={photoSrc}
                        alt={`Attachment ${idx + 1}`}
                        className="w-full h-full object-cover rounded-lg cursor-pointer"
                        onClick={() => setLightboxUrl(photoSrc)}
                        onError={() => setFailedKeys((prev) => ({ ...prev, [photo.key]: true }))}
                      />
                    )}
                    {photo.key.startsWith('pending-') && (
                      <span className="absolute top-1 left-1 bg-orange-500/80 rounded-full p-0.5" title="Waiting to upload">
                        <CloudOff size={10} className="text-white" />
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => setLightboxUrl(photoSrc)}
                      className="absolute bottom-1 right-1 w-6 h-6 bg-black/60 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 sm:opacity-100 transition-opacity"
                    >
                      <Maximize2 size={12} className="text-white" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemove(idx)}
                      className="absolute top-1 right-1 w-5 h-5 bg-black/60 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 sm:opacity-100 transition-opacity"
                    >
                      <X size={12} className="text-white" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* "Add more" button when all category slots have photos but max not reached */}
          {defaultSlots.every((s) => photosForSlot(s.label).length > 0) && value.length < maxPhotos && !uploading && (
            <button
              type="button"
              onClick={() => triggerUpload()}
              className="w-full mt-3 border-2 border-dashed border-fieldiq-border rounded-lg p-3 text-center hover:border-fieldiq-accent transition flex items-center justify-center gap-2"
            >
              <Camera size={16} className="text-fieldiq-muted" />
              <span className="text-xs text-fieldiq-text">Add More Photos</span>
            </button>
          )}

          {/* Uploading indicator for non-slot uploads */}
          {uploading && !uploadingSlot && (
            <div className="flex items-center justify-center gap-2 py-4 text-fieldiq-accent">
              <Loader2 size={20} className="animate-spin" />
              <span className="text-sm">Uploading...</span>
            </div>
          )}

          {value.length >= maxPhotos && (
            <p className="text-xs text-fieldiq-muted text-center py-2 mt-2">Maximum photos reached</p>
          )}
        </>
      )}

      {/* Lightbox */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={() => setLightboxUrl(null)}
        >
          <button
            onClick={() => setLightboxUrl(null)}
            className="absolute top-4 right-4 text-white hover:text-fieldiq-accent transition-colors z-10"
          >
            <X size={28} />
          </button>
          <img
            src={lightboxUrl}
            alt="Photo full view"
            className="max-h-[85vh] max-w-[90vw] rounded-lg object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
